'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';

const BottleneckChart = dynamic(() => import('./BottleneckChart'), { ssr: false });

interface Variant {
  id: string;
  rank: number;
  path: string;
  frequency: number;
  frequencyPct: number;
  conformance: number;
  medianCycleTime: string;
  p95CycleTime: string;
  caseCount: number;
  isHappyPath: boolean;
}

const variants: Variant[] = [
  { id: 'var-001', rank: 1, path: 'Create PO → GR → 3-way Match → Post Invoice → Payment', frequency: 63, frequencyPct: 63, conformance: 100, medianCycleTime: '4.1d', p95CycleTime: '5.8d', caseCount: 759, isHappyPath: true },
  { id: 'var-002', rank: 2, path: 'Create PO → GR → Manual Override → Post Invoice → Payment', frequency: 14, frequencyPct: 14, conformance: 48, medianCycleTime: '7.2d', p95CycleTime: '11.4d', caseCount: 168, isHappyPath: false },
  { id: 'var-003', rank: 3, path: 'Create PO → GR → 3-way Match → Block → Unblock → Post Invoice → Payment', frequency: 9, frequencyPct: 9, conformance: 61, medianCycleTime: '8.8d', p95CycleTime: '14.2d', caseCount: 108, isHappyPath: false },
  { id: 'var-004', rank: 4, path: 'Create PO → Invoice First → GR → 3-way Match → Post Invoice → Payment', frequency: 6, frequencyPct: 6, conformance: 31, medianCycleTime: '9.4d', p95CycleTime: '16.1d', caseCount: 72, isHappyPath: false },
  { id: 'var-005', rank: 5, path: 'Create PO → GR → 3-way Match → Post Invoice → Dispute → Resolve → Payment', frequency: 4, frequencyPct: 4, conformance: 55, medianCycleTime: '12.1d', p95CycleTime: '19.8d', caseCount: 48, isHappyPath: false },
  { id: 'var-006', rank: 6, path: 'Create PO → Cancel → Re-Create PO → GR → 3-way Match → Post Invoice → Payment', frequency: 2, frequencyPct: 2, conformance: 38, medianCycleTime: '11.6d', p95CycleTime: '18.3d', caseCount: 24, isHappyPath: false },
  { id: 'var-007', rank: 7, path: 'Create PO → GR (Partial) → GR (Final) → 3-way Match → Post Invoice → Payment', frequency: 1, frequencyPct: 1, conformance: 72, medianCycleTime: '6.9d', p95CycleTime: '10.2d', caseCount: 12, isHappyPath: false },
  { id: 'var-008', rank: 8, path: 'Create PO → GR → 3-way Match → Post Invoice → Split Payment → Payment (x2)', frequency: 1, frequencyPct: 1, conformance: 68, medianCycleTime: '7.4d', p95CycleTime: '11.9d', caseCount: 9, isHappyPath: false },
];

interface DFGNode {
  id: string;
  label: string;
  frequency: number;
  avgTime: string;
  x: number;
  y: number;
  isBottleneck?: boolean;
}

interface DFGEdge {
  id: string;
  from: string;
  to: string;
  frequency: number;
  avgTime: string;
}

const dfgNodes: DFGNode[] = [
  { id: 'dfg-start', label: 'START', frequency: 1205, avgTime: '—', x: 50, y: 200, },
  { id: 'dfg-po', label: 'Create PO', frequency: 1205, avgTime: '0.4d', x: 170, y: 200 },
  { id: 'dfg-gr', label: 'Goods Receipt', frequency: 1198, avgTime: '2.1d', x: 340, y: 200 },
  { id: 'dfg-match', label: '3-Way Match', frequency: 1024, avgTime: '0.8d', x: 510, y: 150 },
  { id: 'dfg-override', label: 'Manual Override', frequency: 174, avgTime: '3.2d', x: 510, y: 280, isBottleneck: true },
  { id: 'dfg-block', label: 'Invoice Block', frequency: 108, avgTime: '4.1d', x: 680, y: 80, isBottleneck: true },
  { id: 'dfg-post', label: 'Post Invoice', frequency: 1186, avgTime: '0.6d', x: 680, y: 200 },
  { id: 'dfg-pay', label: 'Payment Run', frequency: 1140, avgTime: '1.4d', x: 850, y: 200 },
  { id: 'dfg-end', label: 'END', frequency: 1140, avgTime: '—', x: 980, y: 200 },
];

const dfgEdges: DFGEdge[] = [
  { id: 'dfge-1', from: 'dfg-start', to: 'dfg-po', frequency: 1205, avgTime: '—' },
  { id: 'dfge-2', from: 'dfg-po', to: 'dfg-gr', frequency: 1198, avgTime: '2.1d' },
  { id: 'dfge-3', from: 'dfg-gr', to: 'dfg-match', frequency: 1024, avgTime: '0.8d' },
  { id: 'dfge-4', from: 'dfg-gr', to: 'dfg-override', frequency: 174, avgTime: '3.2d' },
  { id: 'dfge-5', from: 'dfg-match', to: 'dfg-block', frequency: 108, avgTime: '4.1d' },
  { id: 'dfge-6', from: 'dfg-match', to: 'dfg-post', frequency: 916, avgTime: '0.6d' },
  { id: 'dfge-7', from: 'dfg-override', to: 'dfg-post', frequency: 174, avgTime: '0.9d' },
  { id: 'dfge-8', from: 'dfg-block', to: 'dfg-post', frequency: 108, avgTime: '2.3d' },
  { id: 'dfge-9', from: 'dfg-post', to: 'dfg-pay', frequency: 1140, avgTime: '1.4d' },
  { id: 'dfge-10', from: 'dfg-pay', to: 'dfg-end', frequency: 1140, avgTime: '—' },
];

function getNodeCenter(node: DFGNode) {
  const w = node.label === 'START' || node.label === 'END' ? 64 : 120;
  return { cx: node.x + w / 2, cy: node.y + 28 };
}

export default function ProcessMiningContent() {
  const [selectedProcess, setSelectedProcess] = useState('Supplier Invoice');
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [minerAlgo, setMinerAlgo] = useState('heuristic');

  const processes = ['Supplier Invoice', 'Customer Order', 'Financial Close', 'Cash Application'];

  const totalCases = variants.reduce((s, v) => s + v.caseCount, 0);
  const conformanceRate = Math.round(variants.reduce((s, v) => s + v.conformance * v.frequencyPct, 0) / 100);
  const medianCycleTime = '4.8d';
  const variantCount = variants.length;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selectedProcess}
          onChange={(e) => setSelectedProcess(e.target.value)}
          className="bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none cursor-pointer hover:border-primary/40 transition-colors"
        >
          {processes.map((p) => (
            <option key={`proc-${p}`} value={p}>{p}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {[
            { id: 'alpha', label: 'Alpha Miner' },
            { id: 'heuristic', label: 'Heuristic Miner' },
            { id: 'inductive', label: 'Inductive Miner' },
          ].map((algo) => (
            <button
              key={`algo-${algo.id}`}
              onClick={() => setMinerAlgo(algo.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 ${
                minerAlgo === algo.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {algo.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-2 rounded-lg border border-border">
            <Icon name="CalendarIcon" size={12} />
            <span>Jan 1 – Jun 7, 2026</span>
          </div>
          <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted px-3 py-2 rounded-lg border border-border transition-colors">
            <Icon name="AdjustmentsHorizontalIcon" size={12} />
            Filters
          </button>
        </div>
      </div>

      {/* Event Log Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-4 gap-3">
        {[
          { label: 'Total Cases', value: totalCases.toLocaleString(), icon: 'FolderOpenIcon', color: 'text-primary' },
          { label: 'Total Events', value: '47,382', icon: 'BoltIcon', color: 'text-accent' },
          { label: 'Distinct Activities', value: '14', icon: 'CubeIcon', color: 'text-positive' },
          { label: 'Process Variants', value: String(variantCount), icon: 'ArrowsRightLeftIcon', color: 'text-warning' },
        ].map((stat) => (
          <div key={`stat-${stat.label}`} className="bg-card rounded-xl card-glow p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-muted shrink-0 ${stat.color}`}>
              <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={16} />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground font-tabular">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
        {/* DFG Canvas */}
        <div className="xl:col-span-2 bg-card rounded-xl card-glow overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Directly-Follows Graph</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedProcess} · {minerAlgo.charAt(0).toUpperCase() + minerAlgo.slice(1)} Miner · Node size = frequency
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs text-negative bg-negative/10 px-2.5 py-1.5 rounded-lg border border-negative/20">
                <span className="w-2 h-2 rounded-full bg-negative" />
                2 bottlenecks detected
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto canvas-bg p-4">
            <svg width="1060" height="400" viewBox="0 0 1060 400" className="block">
              <defs>
                <marker id="dfg-arrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--muted-foreground)" opacity="0.6" />
                </marker>
                <marker id="dfg-arrow-hot" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--negative)" />
                </marker>
              </defs>

              {/* Edges */}
              {dfgEdges.map((edge) => {
                const fromNode = dfgNodes.find((n) => n.id === edge.from);
                const toNode = dfgNodes.find((n) => n.id === edge.to);
                if (!fromNode || !toNode) return null;
                const from = getNodeCenter(fromNode);
                const to = getNodeCenter(toNode);
                const isHot = fromNode.isBottleneck || toNode.isBottleneck;
                const thickness = Math.max(1, Math.min(4, edge.frequency / 300));
                return (
                  <g key={edge.id}>
                    <line
                      x1={from.cx}
                      y1={from.cy}
                      x2={to.cx}
                      y2={to.cy}
                      stroke={isHot ? 'var(--negative)' : 'var(--border)'}
                      strokeWidth={thickness}
                      markerEnd={isHot ? 'url(#dfg-arrow-hot)' : 'url(#dfg-arrow)'}
                      opacity={isHot ? 0.8 : 0.5}
                    />
                    <text
                      x={(from.cx + to.cx) / 2}
                      y={(from.cy + to.cy) / 2 - 6}
                      fill="var(--muted-foreground)"
                      fontSize="8.5"
                      textAnchor="middle"
                      fontFamily="var(--font-sans)"
                    >
                      {edge.frequency.toLocaleString()}
                    </text>
                  </g>
                );
              })}

              {/* Nodes */}
              {dfgNodes.map((node) => {
                const isStart = node.label === 'START';
                const isEnd = node.label === 'END';
                const isSpecial = isStart || isEnd;
                const w = isSpecial ? 64 : 120;
                const h = 56;
                const isHovered = hoveredNode === node.id;
                const freqRatio = node.frequency / 1205;
                const nodeOpacity = 0.5 + freqRatio * 0.5;

                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, ${node.y})`}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'default' }}
                  >
                    {isSpecial ? (
                      <circle
                        cx={w / 2}
                        cy={h / 2}
                        r={24}
                        fill={isStart ? 'var(--positive)' : 'var(--negative)'}
                        opacity={0.8}
                      />
                    ) : (
                      <>
                        <rect
                          x="0" y="0"
                          width={w}
                          height={h}
                          rx="8"
                          fill={node.isBottleneck ? 'rgba(239,68,68,0.12)' : 'var(--surface-3)'}
                          stroke={node.isBottleneck ? 'var(--negative)' : isHovered ? 'var(--primary)' : 'var(--border)'}
                          strokeWidth={node.isBottleneck || isHovered ? 1.5 : 1}
                          opacity={nodeOpacity}
                        />
                        {node.isBottleneck && (
                          <rect x="0" y="0" width="4" height={h} rx="2" fill="var(--negative)" />
                        )}
                      </>
                    )}

                    {isSpecial ? (
                      <text x={w / 2} y={h / 2 + 4} fill="white" fontSize="9" textAnchor="middle" fontFamily="var(--font-sans)" fontWeight="700">
                        {node.label}
                      </text>
                    ) : (
                      <>
                        <text x="10" y="22" fill={node.isBottleneck ? 'var(--negative)' : 'var(--foreground)'} fontSize="10" fontFamily="var(--font-sans)" fontWeight="600">
                          {node.label}
                        </text>
                        <text x="10" y="36" fill="var(--muted-foreground)" fontSize="9" fontFamily="var(--font-sans)">
                          {node.frequency.toLocaleString()} cases
                        </text>
                        <text x="10" y="48" fill="var(--muted-foreground)" fontSize="9" fontFamily="var(--font-sans)">
                          avg {node.avgTime}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* DFG Legend */}
          <div className="px-5 py-3 border-t border-border flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1.5 rounded-full bg-border" />
              <span className="text-2xs text-muted-foreground">Low frequency</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-2.5 rounded-full bg-border opacity-70" />
              <span className="text-2xs text-muted-foreground">High frequency</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-negative/20 border border-negative/40" />
              <span className="text-2xs text-muted-foreground">Bottleneck node</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1 rounded-full bg-negative opacity-70" />
              <span className="text-2xs text-muted-foreground">Bottleneck edge</span>
            </div>
          </div>
        </div>

        {/* Right Panel — Conformance + Variants */}
        <div className="space-y-4">
          {/* Conformance Panel */}
          <div className="bg-card rounded-xl card-glow p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Conformance Analysis</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-2xs text-muted-foreground mb-1">Overall Conformance</p>
                <p className={`text-xl font-bold font-tabular ${conformanceRate >= 80 ? 'text-positive' : conformanceRate >= 65 ? 'text-warning' : 'text-negative'}`}>
                  {conformanceRate}%
                </p>
              </div>
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-2xs text-muted-foreground mb-1">Median Cycle Time</p>
                <p className="text-xl font-bold font-tabular text-foreground">{medianCycleTime}</p>
              </div>
            </div>

            {/* Conformance breakdown */}
            <div className="space-y-2">
              {[
                { label: 'Happy Path', pct: 63, color: 'var(--positive)' },
                { label: 'Minor Deviations', pct: 16, color: 'var(--warning)' },
                { label: 'Major Deviations', pct: 21, color: 'var(--negative)' },
              ].map((item) => (
                <div key={`conf-${item.label}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-semibold text-foreground font-tabular">{item.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${item.pct}%`, background: item.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottleneck Chart */}
          <BottleneckChart />
        </div>
      </div>

      {/* Variant Table */}
      <div className="bg-card rounded-xl card-glow overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Process Variants</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{variants.length} variants covering {totalCases} cases</p>
          </div>
          <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted px-3 py-1.5 rounded-lg border border-border transition-colors">
            <Icon name="ArrowDownTrayIcon" size={12} />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['#', 'Path', 'Frequency', 'Conformance', 'Median Cycle', 'P95 Cycle', 'Case Count', 'Type'].map((col) => (
                  <th key={`var-col-${col}`} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variants.map((v, idx) => (
                <tr
                  key={v.id}
                  onClick={() => setSelectedVariant(selectedVariant === v.id ? null : v.id)}
                  className={`border-b border-border cursor-pointer transition-colors ${
                    selectedVariant === v.id ? 'bg-primary/5' :
                    idx % 2 === 0 ? 'hover:bg-muted/30' : 'bg-muted/10 hover:bg-muted/30'
                  }`}
                >
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold text-muted-foreground font-tabular">V{v.rank}</span>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="text-xs text-foreground truncate">{v.path}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${v.frequencyPct}%`, background: v.isHappyPath ? 'var(--positive)' : 'var(--primary)' }}
                        />
                      </div>
                      <span className="text-xs font-tabular text-foreground">{v.frequencyPct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold font-tabular ${
                      v.conformance >= 80 ? 'text-positive' : v.conformance >= 50 ? 'text-warning' : 'text-negative'
                    }`}>
                      {v.conformance}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-tabular text-foreground">{v.medianCycleTime}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-tabular text-foreground">{v.p95CycleTime}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-tabular text-foreground">{v.caseCount.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3">
                    {v.isHappyPath ? (
                      <span className="text-2xs bg-positive/15 text-positive px-2 py-0.5 rounded border border-positive/20">Happy Path</span>
                    ) : (
                      <span className="text-2xs bg-muted text-muted-foreground px-2 py-0.5 rounded">Deviation</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}